import { PLANT_BY_ID } from '../data/plants.js';
import { FAMILY_LABEL } from '../data/families.js';
import { decommissionFeeForPlant } from '../engine/advancedCapacity.js';
import { formatMoney } from './format.js';

export function PortfolioList({
  pendingBuilds = [],
  portfolio = [],
  plantsById = PLANT_BY_ID,
  onRemovePending,
  removeDisabled = false,
  // Advanced Capacity Mode retirement (section 16.11). When
  // onRetire is provided, built rows show a Retire toggle.
  pendingRetirements = [],
  onRetire,
  onUnretire,
  // The retirement fee carries an early-closure charge that falls away
  // by Year 3, so the button has to price the year it is in - a player
  // choosing blind is being charged for information they do not have.
  year = 3,
  emptyHint
}) {
  const cumulative = [...portfolio, ...pendingBuilds];

  if (cumulative.length === 0) {
    return (
      <p className="portfolio-empty">
        {emptyHint ?? 'No plants in your portfolio yet.'}
      </p>
    );
  }

  // The first N built instances of a plant id are flagged when the
  // pending retirement list holds N entries for that id.
  const retireCountById = {};
  for (const id of pendingRetirements) {
    retireCountById[id] = (retireCountById[id] ?? 0) + 1;
  }
  const seenById = {};

  return (
    <ul className="portfolio-list" role="list">
      {portfolio.map((id, idx) => {
        const p = plantsById[id];
        const ordinal = seenById[id] ?? 0;
        seenById[id] = ordinal + 1;
        const retiring = ordinal < (retireCountById[id] ?? 0);
        const feeLabel = formatMoney(decommissionFeeForPlant(p, year));
        return (
          <li
            key={`built-${idx}-${id}`}
            className={
              'portfolio-row portfolio-row-built' +
              (retiring ? ' portfolio-row-retiring' : '')
            }
            data-family={p.family}
          >
            <span className="portfolio-name">{p.name}</span>
            <span className="portfolio-family">{FAMILY_LABEL[p.family]}</span>
            <span className="portfolio-opex">{formatMoney(p.opex)} opex</span>
            <span className="portfolio-status">
              {retiring ? 'Retiring' : p.legacy ? 'Inherited' : 'Built'}
            </span>
            {onRetire && !retiring ? (
              <button
                type="button"
                className="portfolio-retire"
                onClick={() => onRetire(id)}
                disabled={removeDisabled}
                aria-label={`Retire ${p.name} for a ${feeLabel} decommissioning fee`}
                title={`Retiring costs ${feeLabel} and frees the plant's grid slot for this build phase. Closing early costs more; the charge falls away by Year 3.`}
                data-testid={`portfolio-retire-${idx}`}
                data-audio-cue="none"
              >
                Retire {feeLabel}
              </button>
            ) : null}
            {onUnretire && retiring ? (
              <button
                type="button"
                className="portfolio-retire"
                onClick={() => onUnretire(pendingRetirements.indexOf(id))}
                disabled={removeDisabled}
                aria-label={`Keep ${p.name}`}
                data-testid={`portfolio-unretire-${idx}`}
                data-audio-cue="none"
              >
                Keep
              </button>
            ) : null}
          </li>
        );
      })}
      {pendingBuilds.map((id, idx) => {
        const p = plantsById[id];
        return (
          <li
            key={`pending-${idx}-${id}`}
            className="portfolio-row portfolio-row-pending"
            data-family={p.family}
          >
            <span className="portfolio-name">{p.name}</span>
            <span className="portfolio-family">{FAMILY_LABEL[p.family]}</span>
            <span className="portfolio-opex">{formatMoney(p.capital)} capital</span>
            <span className="portfolio-status">Pending</span>
            {onRemovePending ? (
              <button
                type="button"
                className="portfolio-remove"
                onClick={() => onRemovePending(idx)}
                disabled={removeDisabled}
                aria-label={`Remove pending ${p.name}`}
                data-testid={`portfolio-remove-${idx}`}
                data-audio-cue="none"
              >
                Remove
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
